import {
    Grid,
    SelectChangeEvent,
    Theme,
    Typography
} from "@mui/material";
import { MFTextField } from "../../../../lib/formik";
import { TextDatePicker } from "../../../onboarding/DatePickerWithTextFeild";
import MFSelectField from "../../../../lib/formik/SelectField";
import MFCheckbox from "../../../../lib/formik/Checkbox";
import {
    useDispatch,
    useSelector
} from "react-redux";
import { RootStateType } from "../../../../redux-store/reducers";
import {
    FormikContextType,
    useFormikContext
} from '../../../../lib/formik/Formik';
import {
    allowDecimalNumbers,
    preventSpecialCharacters
} from "../../../../utils/utilityFunctions";
import dayjs from "../../../../utils/dayjs";
import { MPOWER_ACCOUNT_TYPE_MASTER, UNLISTED_EQUITY_ORDER_DATE_MIN_RANGE } from "../../../../utils/constant";
import { CurrencyConversionToWords } from "../commonComponents";
import FormikSearchDropdown from "../../../../lib/formik/FormikSearchDropdown";
import * as yup from 'yup';
import { numberRegex } from "../../../../utils/regex";
import { GetCounterPartyListResponseBody } from "../../../../redux-store/types/orderManagement";
import {
    Dispatch,
    SetStateAction,
} from "react";

export default function UnlistedEquityForm({
    securitiesData,
    fetchSecuritiesData,
    setValidations,
    isLoading,
    setLoadingStatus,
    counterPartyList,
    fetchCounterPartyList
}: {
    securitiesData: Record<string, any>;
    fetchSecuritiesData: (searchInput: string) => void;
    setValidations: (values: Record<string, any>) => void;
    isLoading: boolean;
    setLoadingStatus: Dispatch<SetStateAction<"" | "apiLoading" | "formSubmitting">>;
    counterPartyList: GetCounterPartyListResponseBody;
    fetchCounterPartyList: (searchInput: string) => void;
}): JSX.Element {
    const dispatch = useDispatch();

    const { currencySymbol, locale = "en-IN" } = useSelector((store: RootStateType) => store.currencyInfo);
    const { businessDate = "" } = useSelector((store: RootStateType) => store.navbarInfo);

    const {
        values,
        setFieldValue,
        setTouched,
        resetForm
    }: FormikContextType<Record<string, any>> = useFormikContext();

    const isHeldAwayAccountType = [MPOWER_ACCOUNT_TYPE_MASTER["HLDAWY"]].includes(MPOWER_ACCOUNT_TYPE_MASTER[`${values?.accountType ?? ""}`.toUpperCase()])
    // NOTE (SAN-21): accountType is populated in Formik via handleAccountChange/handleClientNameChange in the parent
    // (orderEntry/index.tsx) when the user selects a client or account. It is part of baseInitialValues so it is
    // always present in values — the disabled/minDate logic here is safe.
    // NOTE (SAN-03): Stamp duty charges are stored as a locale-formatted string (e.g. "1,234.56").
    // All handlers that read values.charges strip commas via replaceAll(',', '') before parsing.
    // This round-trip is safe for en-IN locale. For other locales where the grouping/decimal separators differ,
    // this could misparse — confirm locale is always en-IN or switch to storing raw numbers.

    // Stamp Duty is computed @ 0.015% of (Rate * Qty)
    const STAMP_DUTY_RATE = 0.00015

    const computeStampDuty = (quantity: number, rate: number): number => {
        if (Number.isNaN(quantity) || Number.isNaN(rate)) {
            return 0
        }
        const result = quantity * rate * STAMP_DUTY_RATE
        return Number.isNaN(result) ? 0 : result
    }

    const handleTextPaste = (event: React.ClipboardEvent<HTMLInputElement>, fieldName: string) => {
        event.preventDefault()
        const clipboardData = event.clipboardData.getData("text")

        const modifiedText = `${clipboardData ?? ""}`?.replaceAll(",", "")

        setFieldValue(fieldName, modifiedText);
    }

    const handleOrderDateChange = (selectedDate: any) => {
        setFieldValue('orderDate', selectedDate?.isValid() ? selectedDate : "", true)
    }

    const handleSecurityChange = async (selectedObj: Record<string, any>) => {
        if (values?.transactionType === "Sell") {

            const quantitySaleable = (!Number.isNaN(Number(selectedObj?.qtySaleable ?? 0)) ? Number(selectedObj?.qtySaleable ?? 0) : 0)

            // if transaction type is Sell,we modify the validation obj to include the Available Quantity validation for quantity field
            setValidations((prevState: Record<string, any>) => ({
                ...prevState,
                quantity: yup
                    .string()
                    .required("Quantity is required")
                    .test('is-number', 'Quantity should be a valid number', function (value) {
                        return !Number.isNaN(Number(value))
                    })
                    .test('whole-number', 'Quantity should be a valid number', function (value) {
                        return numberRegex.test(`${value ?? ""}`);
                    })
                    .test('transaction-type-sell', `Quantity should be less than or equal to ${quantitySaleable}`, function (value) {
                        return Number(value) <= quantitySaleable
                    })
                ,
            }))
        } else if (values?.transactionType !== "Sell") {

            // if transaction type is Buy,we modify the validation obj to remove the Available Quantity validation for quantity field
            setValidations((prevState: Record<string, any>) => ({
                ...prevState,
                quantity: yup
                    .string()
                    .required("Quantity is required")
                    .test('is-number', 'Quantity should be a valid number', function (value) {
                        return !Number.isNaN(Number(value))
                    })
                    .test('whole-number', 'Quantity should be a valid number', function (value) {
                        return numberRegex.test(`${value ?? ""}`);
                    })
            }))
        }

        resetForm({
            values: {
                ...values,
                security: selectedObj,
                issuer: selectedObj?.issuerName ?? "",
                counterParty: null,
                quantity: "",
                // rate: (!Number.isNaN(Number(selectedObj?.marketPrice ?? 0)) ? Number(selectedObj?.marketPrice ?? 0) : 0)?.toLocaleString(locale, {
                //     minimumFractionDigits: 0,
                //     maximumFractionDigits: 4
                // }),
                rate: "",
                stampDuty: false,
                charges: "",
                amount: "",
                // tradeCalculationData: {},
                // settlementBankNumber: null,
                // settlementBank: "",
                // settlementBankIfsc: "",

                // Setting settlementBankSource as security whenever security is selected
                settlementBankSource: selectedObj ? "security" : "",
                // Setting settlementBankNumber as security bank by default
                settlementBankNumber: selectedObj
                    ? {
                        ...(selectedObj ?? {}),
                        bankCode: selectedObj?.bankCode ?? "",
                        bankAccCcy: selectedObj?.bankAccCcy ?? "",
                        bankAccType: selectedObj?.bankAccType ?? "",
                        bankAccNo: selectedObj?.bankAccNo ?? "",
                        bankAccCode: selectedObj?.bankAccCode ?? "",
                        bankName: selectedObj?.bankName ?? "",
                        label: `${selectedObj?.bankAccNo ?? ""} ${selectedObj?.bankName ?? ""}`,
                        value: selectedObj?.bankAccNo ?? ""
                    }
                    : null,
                // Setting settlementBank as security bank name by default
                settlementBank: selectedObj?.bankName ?? "",
                // Setting settlementBankIfsc as security bank ifscCode by default
                settlementBankIfsc: selectedObj?.ifscCode ?? ""
            }
        })
    }

    const handleTransactionTypeChange = async (event: SelectChangeEvent<unknown>) => {
        resetForm({
            values: {
                ...values,
                transactionType: event.target.value,
                security: null,
                issuer: "",
                counterParty: null,
                quantity: "",
                rate: "",
                stampDuty: false, // NOTE (CORR-34): intentional — switching transaction type resets stamp duty. User must re-check if needed for the new type.
                charges: "",
                amount: "",
                // tradeCalculationData: {},
                settlementBankNumber: null,
                settlementBank: "",
                settlementMethod: "",
                settlementBankIfsc: ""
            }
        })
    }

    const handleQuantityOnChange = async (event: React.ChangeEvent<any>) => {
        try {
            // setTouched({ quantity: true }, true)

            const { target: fieldTarget } = event;
            const { value: enteredValue } = fieldTarget;

            // const modifiedEnteredValue = enteredValue?.trim() ?? ""

            const formattedRate = Number(`${values?.rate ?? ""}`?.replaceAll(',', ''))

            // if (modifiedEnteredValue.length === 0 || formattedRate === 0) {
            //     return
            // }

            // setLoadingStatus("apiLoading")

            // const payload = {
            //     // TEMP CHANGES
            //     // portfolioId: "TW100026",
            //     // security: "887",
            //     // quantity: Number(enteredValue ?? ""),
            //     // price: 10, //Rate
            //     // transactionType: "Buy"
            //     // TEMP CHANGES

            //     portfolioId: values?.account ?? "",
            //     security: values?.security?.security ?? "",
            //     quantity: Number(modifiedEnteredValue ?? ""),
            //     price: formattedRate, //Rate
            //     transactionType: values?.transactionType ?? ""
            // }
            // const response = await (dispatch(getTradeCalculation(payload)) as unknown as Promise<GetTradeCalculationResponseBody>);

            // if (!response?.isSuccess) {
            //     dispatch(showError(response?.message ?? "No Data Available"))
            // }

            // if (response?.isSuccess) {
            // const formatedCharges = (
            //     (!Number.isNaN(Number(response?.tradeCalculationData?.feeTotalCapPcy ?? 0)) ? Number(response?.tradeCalculationData?.feeTotalCapPcy ?? 0) : 0)
            //     +
            //     (!Number.isNaN(Number(response?.tradeCalculationData?.feeTotalExpPcy ?? 0)) ? Number(response?.tradeCalculationData?.feeTotalExpPcy ?? 0) : 0)
            // )?.toLocaleString(locale, {
            //     minimumFractionDigits: 0,
            //     maximumFractionDigits: 2
            // })

            // If Stamp Duty is selected, charges = 0.015% of (Rate * Qty), recomputed every time
            // NOTE (CORR-33): formattedCharges uses full-precision stampDutyCharges for amount calculation
            // while displayed charges is rounded to 2dp. Sub-cent difference accepted by client — consistent
            // with CORR-18/CORR-25 note. Same pattern applies in handleRateOnChange and handleStampDutyChange.
            const stampDutyCharges = computeStampDuty(Number(enteredValue), formattedRate)
            const formattedCharges = values?.stampDuty
                ? stampDutyCharges
                : Number(`${values?.charges ?? ""}`?.replaceAll(',', ''))

            let calculatedAmount = Number(`${values?.amount ?? ""}`?.replaceAll(',', ''))

            const areAllFieldsEmpty = `${enteredValue ?? ''}`.length === 0 && `${values?.rate ?? ""}`.length === 0 && (!values?.stampDuty && `${values?.charges ?? ""}`.length === 0)

            // Formula to calculate amount =>
            // Transaction Type: Buy => (Quantity * rate) + charges 
            // Transaction Type: Sell => (Quantity * rate) - charges

            if (!Number.isNaN(Number(enteredValue)) &&
                !Number.isNaN(Number(formattedRate)) &&
                !Number.isNaN(Number(formattedCharges))
            ) {
                if (values.transactionType === "Buy") {
                    calculatedAmount = (Number(enteredValue) * formattedRate) + formattedCharges
                }
                if (values?.transactionType === "Sell") {
                    calculatedAmount = (Number(enteredValue) * formattedRate) - formattedCharges
                }
            }

            resetForm({
                values: {
                    ...values,
                    quantity: enteredValue,
                    // If Stamp Duty is selected, update charges with the computed stamp duty
                    ...(values?.stampDuty ? {
                        charges: areAllFieldsEmpty
                            ? ""
                            : Number(stampDutyCharges)?.toLocaleString(locale, {
                                minimumFractionDigits: 0,
                                maximumFractionDigits: 2
                            })
                    } : {}),
                    // tradeCalculationData: response?.tradeCalculationData ?? {},
                    amount: areAllFieldsEmpty
                        ? ""
                        : Number(calculatedAmount)?.toLocaleString(locale, {
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 2
                        })
                    // amount: (!Number.isNaN(Number(response?.tradeCalculationData?.netAmountTcy ?? 0)) ? Number(response?.tradeCalculationData?.netAmountTcy ?? 0) : 0)?.toLocaleString(locale, {
                    //     minimumFractionDigits: 0,
                    //     maximumFractionDigits: 2
                    // }),
                    // charges: formatedCharges
                }
            })
            // }
        } catch (error) {
            console.log((error as Error)?.message ?? "");
        } finally {
            setLoadingStatus("")
        }
    }

    const handleRateOnChange = async (event: React.ChangeEvent<any>) => {
        try {
            // setTouched({ rate: true }, true)

            const { target: fieldTarget } = event;
            const { value: enteredValue } = fieldTarget;

            // const modifiedEnteredValue = enteredValue?.trim() ?? ""

            const formattedModifiedEnteredValue = Number(enteredValue ?? "")?.toLocaleString(locale, {
                minimumFractionDigits: 0,
                maximumFractionDigits: 4
            })

            // if (
            //     (Number.isNaN(Number(modifiedEnteredValue))) ||
            //     modifiedEnteredValue.length === 0 ||
            //     Number(modifiedEnteredValue) === 0 ||
            //     Number(values?.quantity ?? "") === 0
            // ) {
            //     return
            // }

            // setLoadingStatus("apiLoading")

            // const payload = {
            //     // TEMP CHANGES
            //     // portfolioId: "TW100026",
            //     // security: "887",
            //     // quantity: Number(enteredValue ?? ""),
            //     // price: 10, //Rate
            //     // transactionType: "Buy"
            //     // TEMP CHANGES

            //     portfolioId: values?.account ?? "",
            //     security: values?.security?.security ?? "",
            //     quantity: Number(values?.quantity ?? ""),
            //     price: Number(`${formattedModifiedEnteredValue ?? ""}`?.replaceAll(',', '')), //Rate
            //     transactionType: values?.transactionType ?? ""
            // }
            // const response = await (dispatch(getTradeCalculation(payload)) as unknown as Promise<GetTradeCalculationResponseBody>);

            // if (!response?.isSuccess) {
            //     dispatch(showError(response?.message ?? "No Data Available"))
            // }

            // if (response?.isSuccess) {
            // const formatedCharges = (
            //     (!Number.isNaN(Number(response?.tradeCalculationData?.feeTotalCapPcy ?? 0)) ? Number(response?.tradeCalculationData?.feeTotalCapPcy ?? 0) : 0)
            //     +
            //     (!Number.isNaN(Number(response?.tradeCalculationData?.feeTotalExpPcy ?? 0)) ? Number(response?.tradeCalculationData?.feeTotalExpPcy ?? 0) : 0)
            // )?.toLocaleString(locale, {
            //     minimumFractionDigits: 0,
            //     maximumFractionDigits: 2
            // })

            const formattedQuantity = Number(values?.quantity ?? "")
            const formattedRateValue = Number(`${formattedModifiedEnteredValue ?? ""}`?.replaceAll(',', ''))

            // If Stamp Duty is selected, charges = 0.015% of (Rate * Qty), recomputed every time
            // NOTE (CORR-33): same full-precision/2dp pattern as handleQuantityOnChange — see note there.
            const stampDutyCharges = computeStampDuty(formattedQuantity, formattedRateValue)
            const formattedCharges = values?.stampDuty
                ? stampDutyCharges
                : Number(`${values?.charges ?? ""}`?.replaceAll(',', ''))

            let calculatedAmount = Number(`${values?.amount ?? ""}`?.replaceAll(',', ''))

            const areAllFieldsEmpty = `${values?.quantity ?? ''}`.length === 0 && `${enteredValue ?? ""}`.length === 0 && (!values?.stampDuty && `${values?.charges ?? ""}`.length === 0)

            // Formula to calculate amount =>
            // Transaction Type: Buy => (Quantity * rate) + charges 
            // Transaction Type: Sell => (Quantity * rate) - charges

            if (!Number.isNaN(Number(enteredValue)) &&
                !Number.isNaN(Number(formattedQuantity)) &&
                !Number.isNaN(Number(formattedCharges))
            ) {
                if (values.transactionType === "Buy") {
                    calculatedAmount = (formattedQuantity * formattedRateValue) + formattedCharges
                }
                if (values?.transactionType === "Sell") {
                    calculatedAmount = (formattedQuantity * formattedRateValue) - formattedCharges
                }
            }

            resetForm({
                values: {
                    ...values,
                    rate: enteredValue,
                    // If Stamp Duty is selected, update charges with the computed stamp duty
                    ...(values?.stampDuty ? {
                        charges: areAllFieldsEmpty
                            ? ""
                            : Number(stampDutyCharges)?.toLocaleString(locale, {
                                minimumFractionDigits: 0,
                                maximumFractionDigits: 2
                            })
                    } : {}),
                    // tradeCalculationData: response?.tradeCalculationData ?? {},
                    amount: areAllFieldsEmpty
                        ? ""
                        : Number(calculatedAmount)?.toLocaleString(locale, {
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 2
                        })
                    // amount: (!Number.isNaN(Number(response?.tradeCalculationData?.netAmountTcy ?? 0)) ? Number(response?.tradeCalculationData?.netAmountTcy ?? 0) : 0)?.toLocaleString(locale, {
                    //     minimumFractionDigits: 0,
                    //     maximumFractionDigits: 2
                    // }),
                    // charges: formatedCharges
                }
            })
            // }
        } catch (error) {
            console.log((error as Error)?.message ?? "");
        } finally {
            setLoadingStatus("")
        }
    }

    const handleChargesChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const { target: fieldTarget } = event;
        const { value: enteredValue } = fieldTarget;

        // Formula to calculate amount =>
        // Transaction Type: Buy => (Quantity * rate) + charges 
        // Transaction Type: Sell => (Quantity * rate) - charges

        const formattedModifiedEnteredValue = Number(enteredValue ?? "")?.toLocaleString(locale, {
            minimumFractionDigits: 0,
            maximumFractionDigits: 2
        })

        const formattedQuantity = Number(values?.quantity ?? "")
        const formattedRate = Number(`${values?.rate ?? ""}`?.replaceAll(',', ''))

        let calculatedAmount = Number(`${values?.amount ?? ""}`?.replaceAll(',', ''))

        const areAllFieldsEmpty = `${values?.quantity ?? ''}`.length === 0 && `${values?.rate ?? ""}`.length === 0 && `${enteredValue ?? ""}`.length === 0

        if (
            !Number.isNaN(Number(enteredValue)) &&
            !Number.isNaN(Number(formattedQuantity)) &&
            !Number.isNaN(Number(formattedRate))
        ) {
            if (values.transactionType === "Buy") {
                calculatedAmount = (formattedQuantity * formattedRate) + Number(`${formattedModifiedEnteredValue ?? ""}`?.replaceAll(',', ''))
            }
            if (values?.transactionType === "Sell") {
                calculatedAmount = (formattedQuantity * formattedRate) - Number(`${formattedModifiedEnteredValue ?? ""}`?.replaceAll(',', ''))
            }
        }

        resetForm({
            values: {
                ...values,
                charges: event.target.value,
                amount: areAllFieldsEmpty
                    ? ""
                    : Number(calculatedAmount)?.toLocaleString(locale, {
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 2
                    })
            }
        })
    }

    const handleChargesOnBlur = async (event: React.ChangeEvent<any>) => {

        setTouched({ charges: true }, true)

        const { target: fieldTarget } = event;
        const { value: enteredValue } = fieldTarget;

        const modifiedEnteredValue = enteredValue?.trim() ?? ""

        if (
            (Number.isNaN(Number(modifiedEnteredValue))) ||
            modifiedEnteredValue.length === 0
        ) {
            return
        }

        const formattedModifiedEnteredValue = Number(modifiedEnteredValue ?? "")?.toLocaleString(locale, {
            minimumFractionDigits: 0,
            maximumFractionDigits: 2
        })

        await setFieldValue("charges", formattedModifiedEnteredValue)

        setTouched({ amount: true }, true)
    }

    const handleStampDutyChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const isChecked = event.target.checked

        const formattedQuantity = Number(values?.quantity ?? "")
        const formattedRate = Number(`${values?.rate ?? ""}`?.replaceAll(',', ''))

        // When Stamp Duty is selected, charges = 0.015% of (Rate * Qty); when unselected, clear charges
        const stampDutyCharges = computeStampDuty(formattedQuantity, formattedRate)

        const isAmountComputable = `${values?.quantity ?? ''}`.length > 0 && `${values?.rate ?? ""}`.length > 0

        const chargesValue = isChecked
            ? (isAmountComputable
                ? Number(stampDutyCharges)?.toLocaleString(locale, {
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 2 // NOTE (CORR-18/CORR-25): intentionally 2dp per client requirement. formattedCharges uses full-precision stampDutyCharges for amount calculation. The sub-cent difference between displayed charges and implied charges in amount is accepted by the client — confirmed consistent with CORR-18 note.
                })
                : "")
            : ""

        const formattedCharges = isChecked ? (Number.isNaN(stampDutyCharges) ? 0 : stampDutyCharges) : 0

        let calculatedAmount = Number(`${values?.amount ?? ""}`?.replaceAll(',', ''))

        // Formula to calculate amount =>
        // Transaction Type: Buy => (Quantity * rate) + charges 
        // Transaction Type: Sell => (Quantity * rate) - charges
        if (isAmountComputable) {
            if (values.transactionType === "Buy") {
                calculatedAmount = (formattedQuantity * formattedRate) + formattedCharges
            }
            if (values?.transactionType === "Sell") {
                calculatedAmount = (formattedQuantity * formattedRate) - formattedCharges
            }
        }

        resetForm({
            values: {
                ...values,
                stampDuty: isChecked,
                charges: chargesValue,
                amount: isAmountComputable
                    ? Number(calculatedAmount)?.toLocaleString(locale, {
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 2
                    })
                    : `${values?.amount ?? ""}`
            }
        })
    }

    const handleCounterPartyChange = (selectedObj: Record<string, any>) => {
        const { security } = values

        resetForm({
            values: {
                ...values,
                counterParty: selectedObj,
                // settlementBankNumber: selectedObj
                //     ? {
                //         ...(selectedObj?.banks?.[0] ?? {}),
                //         label: `${selectedObj?.banks?.[0]?.bankAccNo ?? ""} ${selectedObj?.banks?.[0]?.bankName ?? ""}`,
                //         value: selectedObj?.banks?.[0]?.bankAccNo ?? ""
                //     }
                //     : null,
                // settlementBank: selectedObj?.banks?.[0]?.bankName ?? "",
                // settlementBankIfsc: selectedObj?.banks?.[0]?.ifscCode ?? "",

                // Whenever counterParty changes (select/unselect), reset settlementBankSource = security  
                settlementBankSource: "security",
                // Whenever counterParty changes (select/unselect), reset settlementBankNumber = security bank
                settlementBankNumber: security
                    ? {
                        ...(security ?? {}),
                        bankCode: security?.bankCode ?? "",
                        bankAccCcy: security?.bankAccCcy ?? "",
                        bankAccType: security?.bankAccType ?? "",
                        bankAccNo: security?.bankAccNo ?? "",
                        bankAccCode: security?.bankAccCode ?? "",
                        bankName: security?.bankName ?? "",
                        label: `${security?.bankAccNo ?? ""} ${security?.bankName ?? ""}`,
                        value: security?.bankAccNo ?? ""
                    }
                    : null,
                // Whenever counterParty changes (select/unselect), reset settlementBank = security bank name
                settlementBank: security?.bankName ?? "",
                // Whenever counterParty changes (select/unselect), reset settlementBankIfsc = security bank ifsc code
                settlementBankIfsc: security?.ifscCode ?? ""
            }
        })
    }

    const refactoredAmount = `${values?.amount ?? ""}`?.replaceAll(',', '')
    const refactoredCharges = `${values?.charges ?? ""}`?.replaceAll(',', '')
    const refactoredRate = `${values?.rate ?? ""}`?.replaceAll(',', '')
    const refactoredQuantitySaleable = !Number.isNaN(Number(values?.security?.qtySaleable ?? 0)) ? Number(values?.security?.qtySaleable ?? 0) : 0

    return (
        <Grid
            container
            columnSpacing={3}
        >
            {/* <Grid item xs={12} lg={4}>
                <MFTextField
                    name="orderNumber"
                    label="Order Number"
                    placeholder=""
                />
            </Grid> */}
            <Grid item xs={12} lg={4}>
                <TextDatePicker
                    name="orderDate"
                    label="Order Date *"
                    placeholder=""
                    maxDate={
                        businessDate ?
                            dayjs(businessDate, "DD/MM/YYYY").toDate() :
                            new Date()
                    }
                    minDate={
                        isHeldAwayAccountType ?
                            undefined :
                            (businessDate ?
                                dayjs(businessDate, "DD/MM/YYYY").startOf('day').subtract(UNLISTED_EQUITY_ORDER_DATE_MIN_RANGE, 'day').toDate() :
                                dayjs().startOf('day').subtract(UNLISTED_EQUITY_ORDER_DATE_MIN_RANGE, 'day').toDate())
                    }
                    customHandleChange={handleOrderDateChange}
                    disabled={isHeldAwayAccountType || !values.accountType}
                />
            </Grid>
            <Grid item xs={12} lg={4}>
                <MFSelectField
                    name="transactionType"
                    label="Transaction Type *"
                    items={[
                        { name: "Buy" },
                        { name: "Sell" } //call holding api
                    ].map((obj) => ({
                        key: obj.name,
                        value: obj.name,
                    }))}
                    disabled={isLoading || !values?.clientName}
                    onChange={handleTransactionTypeChange}
                />
            </Grid>
            <Grid item xs={12} lg={4}>
                <FormikSearchDropdown
                    name="security"
                    label="Security *"
                    options={securitiesData?.securities?.map((obj: Record<string, any>) => ({
                        ...obj,
                        label: `${obj?.securityName ?? ""}${obj?.isin ? ` (${obj.isin})` : ""}`,
                        value: obj?.security ?? ""
                    })) ?? []}
                    fetchData={fetchSecuritiesData}
                    disabled={isLoading || !values?.transactionType}
                    onChange={handleSecurityChange}
                    filterOptions={(x) => x}
                />
            </Grid>
            <Grid item xs={12} lg={4}>
                <MFTextField
                    name="issuer"
                    label="Issuer"
                    placeholder=""
                    disabled={true}
                    value={values?.issuer ?? ""}
                />
            </Grid>
            <Grid item xs={12} lg={4}>
                <FormikSearchDropdown
                    name="counterParty"
                    label="Counter Party *"
                    options={(counterPartyList?.counterPartyList ?? [])?.map((obj: Record<string, any>) => ({
                        ...obj,
                        label: obj?.description ?? "",
                        value: obj?.identifier ?? ""
                    })) ?? []}
                    fetchData={fetchCounterPartyList}
                    disabled={isLoading || !values?.security}
                    filterOptions={(x) => x}
                    onChange={handleCounterPartyChange}
                />
            </Grid>
            <Grid item xs={12} lg={4} sx={{ position: "relative" }}>
                {values?.transactionType === "Sell" && (
                    <Typography
                        variant="overline_400"
                        sx={{
                            position: "absolute",
                            top: "-5px",
                            right: "0px"
                        }}
                    >
                        Available Quantity: {refactoredQuantitySaleable}
                    </Typography>
                )}
                <MFTextField
                    name="quantity"
                    label="Quantity *"
                    placeholder=""
                    disabled={isLoading || !values?.security}
                    onKeyDown={preventSpecialCharacters}
                    value={values?.quantity ?? ""}
                    onChange={handleQuantityOnChange}
                />
            </Grid>
            <Grid item xs={12} lg={4}>
                <MFTextField
                    name="rate"
                    label="Rate *"
                    placeholder=""
                    startAdornment={values?.rate ? (
                        <Typography
                            variant='body_500'
                            sx={(theme: Theme) => ({
                                color: (isLoading || !values?.security) ? theme.palette.text.secondary : theme.palette.text.primary
                            })}
                        >
                            {String.fromCharCode(parseInt(currencySymbol, 16))}
                        </Typography>
                    ) : undefined}
                    sx={{
                        '&.MuiOutlinedInput-root': {
                            "& fieldset>legend": {
                                fontSize: "0.78em",
                            }
                        }
                    }}
                    onKeyDown={allowDecimalNumbers}
                    disabled={isLoading || !values?.security}
                    onPaste={(event: React.ClipboardEvent<HTMLInputElement>) => handleTextPaste(event, "rate")}
                    formatCurrency
                    formatCurrencyLocaleOptions={{
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 4
                    }}
                    value={values?.rate ?? ""}
                    onChange={handleRateOnChange}
                />
                <CurrencyConversionToWords value={refactoredRate} />
            </Grid>
            <Grid item xs={12} lg={4} sx={{ display: "flex", alignItems: "center" }}>
                <MFCheckbox
                    name="stampDuty"
                    label="Stamp Duty"
                    checked={!!values?.stampDuty}
                    disabled={isLoading || !values?.security}
                    onChange={handleStampDutyChange}
                />
            </Grid>
            <Grid item xs={12} lg={4}>
                <MFTextField
                    name="charges"
                    label="Charges *"
                    placeholder=""
                    startAdornment={values?.charges ? (
                        <Typography
                            variant='body_500'
                            sx={(theme: Theme) => ({
                                color: (isLoading || !values?.security || values?.stampDuty) ? theme.palette.text.secondary : theme.palette.text.primary
                            })}
                        >
                            {String.fromCharCode(parseInt(currencySymbol, 16))}
                        </Typography>
                    ) : undefined}
                    sx={{
                        '&.MuiOutlinedInput-root': {
                            "& fieldset>legend": {
                                fontSize: "0.78em",
                            }
                        }
                    }}
                    onKeyDown={allowDecimalNumbers}
                    disabled={isLoading || !values?.security || values?.stampDuty} // NOTE (SAN-19): charges field is disabled when stampDuty is checked — user cannot manually override the computed stamp duty value, so the two cannot diverge.
                    onPaste={(event: React.ClipboardEvent<HTMLInputElement>) => handleTextPaste(event, "charges")}
                    formatCurrency
                    formatCurrencyLocaleOptions={{
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 2
                    }}
                    value={values?.charges ?? ""}
                    onChange={handleChargesChange}
                    onBlur={handleChargesOnBlur}
                />
                <CurrencyConversionToWords value={refactoredCharges} />
            </Grid>
            <Grid item xs={12} lg={4}>
                <MFTextField
                    name="amount"
                    label="Amount *"
                    placeholder=""
                    startAdornment={values?.amount ? (
                        <Typography
                            variant='body_500'
                            sx={(theme: Theme) => ({
                                color: (isLoading || !values?.security) ? theme.palette.text.secondary : theme.palette.text.primary
                            })}
                        >
                            {String.fromCharCode(parseInt(currencySymbol, 16))}
                        </Typography>
                    ) : undefined}
                    sx={{
                        '&.MuiOutlinedInput-root': {
                            "& fieldset>legend": {
                                fontSize: "0.78em",
                            }
                        }
                    }}
                    onKeyDown={allowDecimalNumbers}
                    disabled={isLoading || !values?.security}
                    formatCurrency
                    formatCurrencyLocaleOptions={{
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 2
                    }}
                    onPaste={(event: React.ClipboardEvent<HTMLInputElement>) => handleTextPaste(event, "amount")}
                    value={values?.amount ?? ""}
                />
                <CurrencyConversionToWords value={refactoredAmount} />
            </Grid>
            <Grid item xs={12}>
                <MFTextField
                    name="remarks"
                    label="Remarks *"
                    placeholder=""
                    multiline
                    inputProps={{ maxLength: 200 }}
                    value={values?.remarks ?? ""}
                    disabled={isLoading}
                />
                <Typography
                    variant="overline_400"
                    sx={(theme: Theme) => ({
                        textAlign: "end",
                        color: theme.palette.text.secondary,
                        marginTop: "-10px"
                    })}
                >
                    Max 200 characters
                </Typography>
            </Grid>
        </Grid>
    )
}